/**
 * Asset: hercules-store-hero.js
 * Section: Hercules Store Hero
 *
 * Responsibilities:
 *  1. Parallax micro-interaction on the product composition images
 *  2. Graceful handling of Shopify Theme Editor section:load / section:rerender events
 *  3. Reduced-motion accessibility compliance
 *
 * Architecture: Module pattern — no globals leaked, safe to re-init on editor reload.
 */

(function () {
  'use strict';

  /* ── CONSTANTS ─────────────────────────────────────────────────── */
  const COMPOSITION_SELECTOR = '[data-hero-composition]';
  const LAYER_SELECTOR = '[data-parallax-layer]';
  const THROTTLE_MS = 16; // ~60 fps cap

  /* ── HELPERS ───────────────────────────────────────────────────── */

  /**
   * Lightweight RAF-based throttle.
   * @param {Function} fn
   * @returns {Function}
   */
  function rafThrottle(fn) {
    let rafId = null;
    return function (...args) {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        fn.apply(this, args);
        rafId = null;
      });
    };
  }

  /**
   * Check if the user prefers reduced motion.
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── PARALLAX CONTROLLER ───────────────────────────────────────── */
  class HeroParallax {
    /**
     * @param {HTMLElement} compositionEl — the [data-hero-composition] wrapper
     */
    constructor(compositionEl) {
      this.el = compositionEl;
      this.layers = Array.from(compositionEl.querySelectorAll(LAYER_SELECTOR));
      this._handleMouseMove = rafThrottle(this._onMouseMove.bind(this));
      this._handleDestroy = this.destroy.bind(this);
      this._active = false;
    }

    init() {
      if (prefersReducedMotion() || !this.layers.length) return;

      document.addEventListener('mousemove', this._handleMouseMove, { passive: true });
      this._active = true;
    }

    destroy() {
      if (!this._active) return;
      document.removeEventListener('mousemove', this._handleMouseMove);

      // Reset transforms
      this.layers.forEach((layer) => {
        layer.style.transform = '';
      });

      this._active = false;
    }

    /** @param {MouseEvent} e */
    _onMouseMove(e) {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;

      // Normalised delta: -0.5 to +0.5
      const nx = (e.clientX - cx) / window.innerWidth;
      const ny = (e.clientY - cy) / window.innerHeight;

      this.layers.forEach((layer) => {
        const depth = parseFloat(layer.dataset.parallaxLayer) || 1;
        const factorX = nx * depth * 12; // px range per depth unit
        const factorY = ny * depth * 8;

        /*
         * Preserve any CSS-defined base transform (e.g. translateY on accent)
         * by reading the data attribute set by CSS class logic below.
         */
        const baseTransform = layer.dataset.baseTransform || '';
        layer.style.transform = `${baseTransform} translate(${factorX}px, ${factorY}px)`;
      });
    }
  }

  /* ── SECTION MANAGER ───────────────────────────────────────────── */

  /**
   * Registry to hold section instances keyed by section container id.
   * Prevents duplicate listeners on theme editor re-renders.
   * @type {Map<string, HeroParallax>}
   */
  const instances = new Map();

  /**
   * Initialise all hero compositions found within a root element.
   * @param {Document | HTMLElement} root
   */
  function initInRoot(root) {
    const compositions = root.querySelectorAll(COMPOSITION_SELECTOR);

    compositions.forEach((compositionEl) => {
      // Derive a stable key from the closest section wrapper
      const section =
        compositionEl.closest('.hsh-section-wrapper, .shopify-section') || compositionEl;
      const key = section.id || compositionEl.dataset.sectionId || 'hsh-hero-default';

      // Destroy any previous instance for this section
      if (instances.has(key)) {
        instances.get(key).destroy();
        instances.delete(key);
      }

      const parallax = new HeroParallax(compositionEl);
      parallax.init();
      instances.set(key, parallax);
    });
  }

  /**
   * Tear down all instances within a section (called on section:deselect / unload).
   * @param {string} sectionId
   */
  function destroySection(sectionId) {
    if (instances.has(sectionId)) {
      instances.get(sectionId).destroy();
      instances.delete(sectionId);
    }
  }

  /* ── SHOPIFY THEME EDITOR EVENT BRIDGE ────────────────────────── */
  /*
   * Shopify fires custom events on the document when sections are
   * reloaded in the theme editor. We listen and re-init accordingly.
   * See: https://shopify.dev/docs/themes/architecture/sections/section-events
   */

  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target;
    initInRoot(sectionEl);
  });

  document.addEventListener('shopify:section:reorder', (e) => {
    const sectionEl = e.target;
    initInRoot(sectionEl);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionId = e.detail?.sectionId;
    if (sectionId) destroySection(sectionId);
  });

  /* ── INITIAL BOOT ──────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initInRoot(document));
  } else {
    initInRoot(document);
  }
})();
